import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import {
	formatCountdown,
	formatFundingRate,
	formatPrice,
	formatSignedPercent,
	formatUsd,
	getFundingCountdownSeconds,
	toNumber,
} from "#/lib/formatters";
import { PriceFlash } from "./price-flash";

type MarketInfoData = {
	coin: string;
	displayName: string;
	displaySymbol: string;
	markPrice: string | number | null | undefined;
	previousDayPrice: string | number | null | undefined;
	oraclePrice: string | number | null | undefined;
	priceChangePercent: string | number | null | undefined;
	dayNotionalVolume: string | number | null | undefined;
	openInterestValue: string | number | null | undefined;
	predictedFundingRate: string | number | null | undefined;
};

export type MarketInfoMetric = {
	id:
		| "mark"
		| "oracle"
		| "change24h"
		| "volume24h"
		| "openInterest"
		| "fundingCountdown";
	label: string;
	value: ReactNode;
	valueClassName?: string;
	description?: string;
};

export function useMarketInfo(marketData: MarketInfoData) {
	const [countdownSeconds, setCountdownSeconds] = useState(
		getFundingCountdownSeconds,
	);

	useEffect(() => {
		const timer = window.setInterval(() => {
			setCountdownSeconds(getFundingCountdownSeconds());
		}, 1000);

		return () => window.clearInterval(timer);
	}, []);

	const markPrice = toNumber(marketData.markPrice);
	const previousDayPrice = toNumber(marketData.previousDayPrice);
	const absoluteChange = markPrice - previousDayPrice;
	const fundingRate = toNumber(marketData.predictedFundingRate);
	const changePrefix = absoluteChange > 0 ? "+" : "";

	const priceChangeLabel = `${changePrefix}${formatPrice(absoluteChange)} / ${formatSignedPercent(
		marketData.priceChangePercent,
	)}`;

	const hasData = markPrice > 0;

	const metrics: MarketInfoMetric[] = [
		{
			id: "mark",
			label: "market-info.mark",
			value: hasData ? (
				<PriceFlash
					value={markPrice}
					display={formatPrice(marketData.markPrice)}
					resetKey={marketData.coin}
				/>
			) : (
				"-"
			),
			description: "market-info.mark-description",
		},
		{
			id: "oracle",
			label: "market-info.oracle",
			value: hasData ? formatPrice(marketData.oraclePrice) : "-",
			description: "market-info.oracle-description",
		},
		{
			id: "change24h",
			label: "market-info.24h-change",
			value: hasData ? priceChangeLabel : "-",
			valueClassName:
				absoluteChange >= 0 ? "text-trade-green" : "text-trade-red",
		},
		{
			id: "volume24h",
			label: "market-info.24h-volume",
			value: hasData ? formatUsd(toNumber(marketData.dayNotionalVolume)) : "-",
		},
		{
			id: "openInterest",
			label: "market-info.open-interest",
			value: hasData ? formatUsd(toNumber(marketData.openInterestValue)) : "-",
			description: "market-info.open-interest-description",
		},
		{
			id: "fundingCountdown",
			label: "market-info.funding-countdown",
			description: "market-info.funding-countdown-description",
			value: (
				<>
					<span>{formatFundingRate(marketData.predictedFundingRate)}</span>
					<span className="text-[var(--color-theme-static-black)] dark:text-[var(--color-theme-static-white)]">
						{" "}
						/ {formatCountdown(countdownSeconds)}
					</span>
				</>
			),
			valueClassName: fundingRate >= 0 ? "text-trade-green" : "text-trade-red",
		},
	];

	return {
		symbolLabel: marketData.displaySymbol,
		symbolBadgeLabel: marketData.coin.slice(0, 1),
		metrics,
	};
}
